import { Plugin } from 'ckeditor5';
import type { Writer, Element } from 'ckeditor5';

interface HtmlAttributes {
  styles?: Record<string, string>;
  classes?: string[];
  attributes?: Record<string, string>;
}

/**
 * 리스트 생성/해제 시 margin-left를 이동하는 플러그인 (v3)
 *
 * CKEditor5 43.x의 DocumentList 구조에서 동작:
 * - paragraph에 listIndent, listType 속성으로 리스트 관리
 * - li 태그는 View에서만 생성되는 래퍼
 * - htmlLiAttributes를 사용하여 li 스타일을 Model에 저장
 *
 * 동작:
 *   - 리스트 생성 시: blockIndent(p의 margin-left) → htmlLiAttributes로 이동
 *   - 리스트 해제 시: htmlLiAttributes → blockIndent로 이동
 *   - 리스트 내 엔터 시: htmlLiAttributes가 Model에 저장되어 자동 유지
 */
export default class ListIndentFixer extends Plugin {
  private _savedMargins: Map<string, string> = new Map();
  private _wasInList: Map<string, boolean> = new Map();

  static get pluginName() {
    return 'ListIndentFixer' as const;
  }

  init(): void {
    this._attachListCommandListener('bulletedList');
    this._attachListCommandListener('numberedList');
  }

  private _attachListCommandListener(commandName: string): void {
    const command = this.editor.commands.get(commandName);
    if (!command) return;

    // 명령 실행 전: 현재 상태 저장
    command.on('execute', () => this._saveCurrentState(), { priority: 'high' });

    // 명령 실행 후: margin-left 이동 처리
    command.on('execute', () => {
      setTimeout(() => this._handleListToggle(), 0);
    }, { priority: 'low' });
  }

  /**
   * 명령 실행 전 현재 상태 저장
   */
  private _saveCurrentState(): void {
    const selection = this.editor.model.document.selection;
    const blocks = Array.from(selection.getSelectedBlocks());

    this._savedMargins.clear();
    this._wasInList.clear();

    blocks.forEach((block, index) => {
      const key = `block_${index}`;
      const isInList = block.hasAttribute('listIndent');
      this._wasInList.set(key, isInList);

      // margin-left 저장 (blockIndent 또는 htmlLiAttributes에서)
      const marginLeft = this._getMarginLeft(block, isInList);
      if (marginLeft) {
        this._savedMargins.set(key, marginLeft);
      }
    });
  }

  /**
   * 블록에서 margin-left 값 추출
   */
  private _getMarginLeft(block: Element, isInList: boolean): string | undefined {
    if (isInList) {
      // 리스트 상태: htmlLiAttributes에서 찾기
      const liAttrs = block.getAttribute('htmlLiAttributes') as HtmlAttributes | undefined;
      return liAttrs?.styles?.['margin-left'];
    } else {
      // 일반 상태: blockIndent에서 찾기
      return block.getAttribute('blockIndent') as string | undefined;
    }
  }

  /**
   * 리스트 토글 후 margin-left 이동 처리
   */
  private _handleListToggle(): void {
    const model = this.editor.model;
    const selection = model.document.selection;

    model.change(writer => {
      const blocks = Array.from(selection.getSelectedBlocks());

      blocks.forEach((block, index) => {
        const key = `block_${index}`;
        const isInList = block.hasAttribute('listIndent');
        const wasInList = this._wasInList.get(key) || false;
        const savedMargin = this._savedMargins.get(key);

        if (isInList && !wasInList && savedMargin) {
          // 리스트로 변환됨: blockIndent → htmlLiAttributes로 이동
          this._moveMarginToLi(writer, block, savedMargin);
        } else if (!isInList && wasInList && savedMargin) {
          // 리스트 해제됨: htmlLiAttributes → blockIndent로 이동
          this._moveMarginToP(writer, block, savedMargin);
        }
      });

      this._savedMargins.clear();
      this._wasInList.clear();
    });
  }

  /**
   * blockIndent에서 htmlLiAttributes로 margin-left 이동
   */
  private _moveMarginToLi(writer: Writer, block: Element, marginLeft: string): void {
    // 1. blockIndent 제거
    if (block.hasAttribute('blockIndent')) {
      writer.removeAttribute('blockIndent', block);
    }

    // 2. htmlLiAttributes에 margin-left 저장
    const liAttrs = block.getAttribute('htmlLiAttributes') as HtmlAttributes | undefined || {};
    writer.setAttribute('htmlLiAttributes', {
      ...liAttrs,
      styles: { ...liAttrs.styles, 'margin-left': marginLeft }
    }, block);
  }

  /**
   * htmlLiAttributes에서 blockIndent로 margin-left 이동
   */
  private _moveMarginToP(writer: Writer, block: Element, marginLeft: string): void {
    // blockIndent 복원
    writer.setAttribute('blockIndent', marginLeft, block);
  }
}
